import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { ActivityService } from '@omni/services/activity/activity.service';
import { UIService } from '@omni/services/ui/ui.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';

/**
 * Activity creation flow. We don't persist anything from the tool itself;
 * we route the user into the new-activity form pre-populated via
 * `aiAssistantData` so they can review/save in the existing UI.
 */
@Injectable({ providedIn: 'root' })
export class ActivityTools {
  private readonly activityService = inject(ActivityService);
  private readonly uiService = inject(UIService);
  private readonly navService = inject(NavigationService);

  @AITool('create_activity', 'Create a new activity or meeting', [
    { name: 'subject', type: 'string', description: 'The subject or title of the activity', required: true },
    { name: 'description', type: 'string', description: 'Optional detailed description', required: false },
    {
      name: 'activityType',
      type: 'string',
      description: 'Type of activity',
      required: true,
      enum: ['meeting', 'appointment', 'call', 'task', 'email'],
    },
    {
      name: 'startDate',
      type: 'string',
      description: 'Start date/time in ISO format (e.g., "2026-01-13T14:00:00")',
      required: true,
    },
    { name: 'location', type: 'string', description: 'Optional location or meeting link', required: false },
  ])
  async createActivity(
    subject: string,
    description = '',
    activityType: 'meeting' | 'appointment' | 'call' | 'task' | 'email',
    startDate: string,
    location = '',
  ): Promise<any> {
    try {
      if (this.navService.getCurrentPageName() !== PageName.GoAppActivitiesPageComponent) {
        await this.navService.setRootWithTracking(
          (await import('@omni/pages/go-app-activities-page/go-app-activities-page.component'))
            .GoAppActivitiesPageComponent,
          PageName.GoAppActivitiesPageComponent,
          { __source: 'ai-assistant' },
          PageName.GoAppActivitiesPageComponent,
        );
      }

      this.activityService.activitySource = null;

      this.uiService.showRightPane = true;
      this.uiService.showRightPaneOnly = false;

      await this.navService.pushChildNavPageWithPageTracking(
        (await import('@omni/components/activity/new-activity/new-activity')).NewActivityComponent,
        PageName.NewActivityComponent,
        PageName.GoAppActivitiesPageComponent,
        {
          from: PageName.GoAppHomeComponent,
          aiAssistantData: {
            subject,
            description,
            activityType: activityType.charAt(0).toUpperCase() + activityType.slice(1),
            startDate: new Date(startDate),
            location,
          },
        },
      );

      return {
        success: true,
        message: `Activity form opened with "${subject}" pre-filled. Please review and save.`,
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create activity' };
    }
  }
}
